// Markup from design/kit/components/ConfirmPopup: the small popup that asks once more.
import { useEffect, useRef } from 'react';
import { useModalLayer } from './modal-layer';

type ConfirmPopupProps = {
  title: string;
  line: string;
  action: string;
  cancel?: string;
  danger?: boolean;
  /** While the confirmed action is running: the action button is disabled, so it is sent once. */
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

export function ConfirmPopup({
  title,
  line,
  action,
  cancel = 'Cancel',
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmPopupProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  useModalLayer(boxRef);

  useEffect(() => {
    // Cancel has the focus, so Enter never confirms by accident.
    cancelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // The confirm is the top-most layer: Esc closes it and nothing under it.
        e.stopImmediatePropagation();
        onCancel();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  return (
    <>
      <button type="button" className="scrim cf-scrim" aria-label={cancel} onClick={onCancel} />
      <div
        ref={boxRef}
        className="modal cfm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cf-title"
        aria-describedby="cf-line"
      >
        <div className="cf-b">
          <h2 id="cf-title" className="dtitle">
            {title}
          </h2>
          <p id="cf-line" className="m0 t13 text2 lh">
            {line}
          </p>
        </div>
        <div className="m-foot">
          <button ref={cancelRef} type="button" className="btn" onClick={onCancel}>
            {cancel}
          </button>
          <button
            type="button"
            className={danger ? 'btn btn-dfill ml-auto' : 'btn btn-primary ml-auto'}
            disabled={busy}
            onClick={onConfirm}
          >
            {action}
          </button>
        </div>
      </div>
    </>
  );
}
